import type { ReactNode } from 'react'
import type { useStagedFilesUpload } from '../hooks/useStagedFilesUpload'
import { Button } from '@/components/button/Button'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { ConflictModal } from '@/user_files/drive/components/ConflictModal'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/Popover'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { Upload, InfoCircle, ShieldCheck } from '@/assets/icons'
import { kPaxtonAiName } from '@/constants/constants-strings'
import { ADD_FILES_MODAL_TAB, type AddFilesModalInitialTab } from '@/chat-common/components/source-dialogs/AddFilesModal'
import { MatterAssetFileSourceMenu } from './MatterAssetFileSourceMenu'

/** Shared Add Files surface: dropzone, source menu, progress, upload conflicts, errors, and submission. */
export function FileStagingPage({
  matterId,
  upload,
  title,
  description,
  headerExtra,
  beforeFiles,
  children,
  totalStaged,
  readyStagedCount,
  failedStagedCount = 0,
  onOpenFilePicker,
  onClearAll,
  onSubmit,
  submitDisabled,
  submitPending,
  submitLabel,
  pendingLabel,
  error,
  uploadInformation,
}: {
  matterId: string | undefined
  upload: ReturnType<typeof useStagedFilesUpload>
  title: string
  description: string
  headerExtra?: ReactNode
  beforeFiles?: ReactNode
  children: ReactNode
  totalStaged: number
  readyStagedCount: number
  failedStagedCount?: number
  onOpenFilePicker: (tab: AddFilesModalInitialTab) => void
  onClearAll: () => void
  onSubmit: () => void
  submitDisabled: boolean
  submitPending: boolean
  submitLabel: string
  pendingLabel: string
  error?: string | null
  uploadInformation?: ReactNode
}) {
  const dropzoneRootProps = upload.getRootProps()
  const progressTotal = totalStaged - failedStagedCount
  return (
    <>
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col items-center p-4 lg:p-6" {...dropzoneRootProps}>
        <input {...upload.getInputProps()} />
        <input ref={upload.fileInputRef} type="file" accept={upload.inputAccept} multiple hidden onChange={upload.onFilesPicked} />

        <div className="w-full max-w-[946px] flex-1 min-h-0 flex flex-col bg-brand-neutral-50 border border-brand-neutral-200 rounded-md overflow-hidden md:rounded-lg">
          {/* Header */}
          <div className="flex flex-col gap-3 px-6 pt-6 pb-4 md:flex-row md:items-start md:justify-between md:gap-6">
            <div className="flex flex-col gap-1">
              <h1 className="font-rethink-sans text-lg font-semibold leading-7 text-brand-neutral-950">{title}</h1>
              <p className="hidden text-base font-normal leading-6 tracking-[-0.32px] text-brand-neutral-950 md:block">{description}</p>
            </div>
            <div className="flex items-center gap-3.5 shrink-0">
              <Button className="md:hidden" size="sm" variant="outlined" color="primary" onClick={upload.open} disabled={upload.disabled}>
                Upload
              </Button>
              <Button className="max-md:hidden md:inline-flex" variant="outlined" color="primary" onClick={upload.open} disabled={upload.disabled}>
                Upload
              </Button>
              <Button
                className="md:hidden"
                size="sm"
                variant="filled"
                color="primary"
                disabled={submitPending}
                onClick={() => onOpenFilePicker(ADD_FILES_MODAL_TAB.MATTER_FILES)}
              >
                Matter Files
              </Button>
              <Button
                className="max-md:hidden md:inline-flex"
                variant="filled"
                color="primary"
                disabled={submitPending}
                onClick={() => onOpenFilePicker(ADD_FILES_MODAL_TAB.MATTER_FILES)}
              >
                Matter Files
              </Button>
            </div>
          </div>

          {headerExtra && <div className="px-6 pb-4">{headerExtra}</div>}
          {error && (
            <div className="px-6 pb-4">
              <InlineMessage variant="error">{error}</InlineMessage>
            </div>
          )}
          {/* Body: included drawer, then dropzone when empty or the staged list */}
          <div className={`flex-1 min-h-0 overflow-y-auto px-6 pb-0 flex flex-col gap-2 md:pb-6 ${totalStaged > 0 ? 'md:overflow-hidden' : ''}`}>
            {beforeFiles}
            {totalStaged === 0 ? (
              <div
                className={`w-full flex-1 min-h-[160px] rounded-lg flex flex-col items-center justify-center gap-3.5 px-4 pt-4 pb-9 transition-colors md:min-h-[320px] ${
                  upload.isDragActive ? 'bg-brand-50 outline-2 outline-dashed outline-brand-500' : ''
                }`}
              >
                <Upload className="w-6 h-6 text-brand-neutral-600" />
                <div className="flex flex-col items-center gap-2 text-center">
                  <p className="font-rethink-sans text-[15px] font-semibold leading-[25px] text-brand-neutral-600">Drag &amp; Drop to Upload or</p>
                  <MatterAssetFileSourceMenu matterId={matterId} onUpload={upload.open} onOpenFilePicker={onOpenFilePicker} />
                </div>
              </div>
            ) : (
              <div className="shrink-0 flex flex-col gap-3 pt-2 md:flex-1 md:min-h-0">
                <div className="shrink-0 flex flex-col gap-2 pb-4 pt-2">
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-rethink-sans text-base font-semibold leading-6 text-brand-neutral-950">Documents</p>
                    <p className="text-xs font-medium text-brand-neutral-600">
                      {failedStagedCount > 0 ? (
                        <>
                          {readyStagedCount} ready · {failedStagedCount} failed
                          {progressTotal > readyStagedCount && ` · ${progressTotal - readyStagedCount} pending`}
                        </>
                      ) : (
                        <>
                          {readyStagedCount} of {totalStaged}
                        </>
                      )}
                    </p>
                  </div>
                  {progressTotal > 0 && (
                    <div
                      role="progressbar"
                      aria-label="Documents ready"
                      aria-valuemin={0}
                      aria-valuemax={progressTotal}
                      aria-valuenow={readyStagedCount}
                      className="h-0.5 w-full bg-brand-neutral-200 rounded-full overflow-hidden"
                    >
                      <div className="h-full bg-brand-500 transition-all" style={{ width: `${Math.round((readyStagedCount / progressTotal) * 100)}%` }} />
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-3 [&>*]:shrink-0 md:flex-1 md:min-h-0 md:overflow-y-auto">{children}</div>
              </div>
            )}
          </div>

          {/* Footer CTA bar — wraps by available width so the primary CTA never clips off-screen */}
          <div className="border-t pl-4 pr-6 py-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-4">
            {uploadInformation ?? (
              <div className="flex min-h-7 items-center gap-1 px-0.5 md:min-h-0 md:px-0">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-1 whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium text-brand-neutral-600 hover:text-brand-neutral-800 transition-colors"
                    >
                      <InfoCircle className="w-3.5 h-3.5 shrink-0" />
                      PDF files only
                    </button>
                  </PopoverTrigger>
                  <PopoverContent width="auto" sideOffset={5} align="start" className="max-w-xs">
                    <p className="text-sm">
                      Currently supports PDF format.
                      <br />
                      Additional file types coming soon.
                      <br />
                      Max file size 1GB.
                    </p>
                  </PopoverContent>
                </Popover>
                <span className="h-4 w-px bg-brand-neutral-300 shrink-0" aria-hidden="true" />
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-1 whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium text-brand-neutral-600 hover:text-brand-neutral-800 transition-colors"
                    >
                      <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                      HIPAA
                    </button>
                  </PopoverTrigger>
                  <PopoverContent width="auto" sideOffset={5} align="start" className="max-w-xs">
                    <p className="text-sm">
                      {kPaxtonAiName} rigorously adheres to SOC 2 and HIPAA standards. Your documents and data are secure, and never used to train{' '}
                      {kPaxtonAiName}.
                    </p>
                  </PopoverContent>
                </Popover>
              </div>
            )}
            <div className="flex items-center gap-4 shrink-0 ml-auto md:gap-3">
              <Button className="whitespace-nowrap" variant="outlined" color="destructive" onClick={onClearAll} disabled={totalStaged === 0 || submitPending}>
                Clear All
              </Button>
              <Button className="whitespace-nowrap" variant="filled" color="primary" onClick={onSubmit} disabled={submitDisabled}>
                {submitPending ? (
                  <span className="inline-flex items-center gap-2">
                    <CircularProgressContinuousSized size={14} thickness={4} />
                    {pendingLabel}
                  </span>
                ) : (
                  submitLabel
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ConflictModal {...upload.conflictDialog} operation="upload" />
    </>
  )
}
